import React, { CSSProperties, FunctionComponent, useMemo, useState } from "react";
import { v4 as uuid } from "uuid";
import AsyncSelect from "react-select/async";
import AsyncCreatableSelect from "react-select/async-creatable";
import debounce from "lodash/debounce";
import styled from "styled-components";
import ErrorWrapper, { ErrorWrapperProps } from "./ErrorWrapper";
import { selectStyles } from "./Dropdowns";
import { ReactSelectOptions } from "../types";
import { ColorVariables } from "../Colors";
import { getMobileBreakpoint } from "../Styles/globals";
import { CrossIcon } from "../Images/icons";

// NOTE: The value will clear once you focus on the input. If we want to do a multiselect autocomplete,
//  we should not clear the input on focus.

export const StyledActionIcon = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  margin-right: 14px;
  margin-top: 18px;
  svg {
    cursor: pointer;
    width: 12px;
    height: 12px;
    &:hover {
      opacity: 0.8;
    }
  }
`;

export const StyledAutocompleteOptionLabel = styled.div`
  * {
    margin-bottom: 8px;
  }
  *:last-child {
    margin-bottom: 0;
  }
  .title {
    color: ${ColorVariables.coco};
    line-height: initial;
  }
  .subtitle {
    color: ${ColorVariables.coco9};
    line-height: initial;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
  @media (max-width: ${getMobileBreakpoint}) {
    * {
      margin-bottom: 4px;
    }
  }
`;

const StyledAutoComplete = styled.div`
  .react-select__dropdown-indicator {
    display: none;
  }
`;

function formatOptionLabelDefault(option: ReactSelectOptions[0]) {
  return (
    <StyledAutocompleteOptionLabel>
      <p className="title medium">{option.label}</p>
      {option.subtitle && <p className="subtitle xsmall">{option.subtitle}</p>}
    </StyledAutocompleteOptionLabel>
  );
}

export type AutocompleteProps = ErrorWrapperProps &
  Partial<{
    creatable: boolean;
    placeholder: string;
    formatOptionLabel: any; // TODO: find the types of formatOptionLabel
    noOptionsMessage: string;
    resultsLimit: number;
    cancellable: boolean;
    onBlur: ((e: React.FocusEvent<HTMLElement>) => any) | undefined;
    onChange: ((val: string, isNew: boolean) => any) | undefined;
    onLoadOptions: ((val: string) => Promise<ReactSelectOptions>) | undefined;
    value: string | number;
    options: ReactSelectOptions;
    helperTitle: string;
    helperBody: JSX.Element;
    menuPlacement: "auto" | "top" | "bottom";
    titleColor: string;
    creatableOptionPosition: "first" | "last";
  }> & {
    name: string;
  };

const Autocomplete = ({
  cancellable = true,
  creatable = false,
  formatOptionLabel = formatOptionLabelDefault,
  resultsLimit = 10,
  title = "",
  noOptionsMessage = "",
  name = "",
  showLock = false,
  error = false,
  caption = "",
  captionColor = "",
  captionAlign = "flex-start",
  onCaptionClick = undefined,
  required = false,
  className = "",
  placeholder = "",
  value = "",
  options = [],
  onBlur = undefined,
  onChange = undefined,
  onLoadOptions = undefined,
  helperBody,
  helperTitle,
  menuPlacement = undefined,
  titleColor = undefined,
  creatableOptionPosition = "first",
}: AutocompleteProps) => {
  const [active, setActive] = useState(false);
  const selectId = useMemo(() => uuid(), []);

  function toggleHighlight() {
    setActive(!active);
  }

  function handleOnFocus() {
    if (onChange) {
      onChange("", false);
    }
    toggleHighlight();
  }

  function handleOnBlur(e: React.FocusEvent<HTMLElement>) {
    toggleHighlight();
    if (onBlur) {
      onBlur(e);
    }
  }

  function handleOnChange(selectedOption?: ReactSelectOptions[0]) {
    if (onChange && selectedOption && selectedOption.__isNew__ && selectedOption.value) {
      onChange(
        selectedOption.value.toString().charAt(0).toUpperCase() + selectedOption.value.toString().slice(1),
        !!selectedOption.__isNew__
      );
      return;
    }
    if (onChange) {
      onChange(selectedOption ? selectedOption.value.toString() : "", !!selectedOption?.__isNew__);
    }
  }

  function search(searchOptions: ReactSelectOptions) {
    const results = searchOptions.filter((option) => option.label.toLowerCase());
    return results.slice(0, resultsLimit);
  }

  // A little silly, but we can't return a promise here. Using the .then and .catch is intentional.
  // Ref: https://github.com/JedWatson/react-select/issues/3075#issuecomment-506647171
  const handleLoadOptions = React.useCallback(
    debounce((val: string, callback) => {
      if (onLoadOptions) {
        onLoadOptions(val)
          .then((results) => {
            callback(search(results));
          })
          .catch(() => {
            callback([]);
          });
        return;
      }
      callback(search(options));
    }, 300),
    []
  );

  // TODO: find the type of select props
  const selectProps: any = {
    allowCreateWhileLoading: false,
    blurInputOnSelect: true,
    cacheOptions: false,
    loadOptions: handleLoadOptions,
    formatOptionLabel,
    createOptionPosition: creatableOptionPosition,
    formatCreateLabel(label) {
      return `${label.charAt(0).toUpperCase() + label.slice(1)}`;
    },
    onFocus: handleOnFocus,
    onBlur: handleOnBlur,
    onChange: handleOnChange,
    placeholder,
    id: selectId,
    menuShouldScrollIntoView: false,
    menuPlacement: menuPlacement || "bottom",
    noOptionsMessage: () => noOptionsMessage || null,
    styles: selectStyles(),
    classNamePrefix: "react-select",
    error,
    defaultOptions: options,
    isClearable: cancellable,
    value:
      options.find((option) => option.value === value) || creatable
        ? {
            key: value,
            label: value,
          }
        : "",
    isActive: active,
  };
  const CustomClearText: FunctionComponent<{ show: boolean }> = ({ show }) => (
    <StyledActionIcon data-name="cancellable">{show ? <CrossIcon /> : <></>}</StyledActionIcon>
  );
  const ClearIndicator = (props: any) => {
    const {
      children = <CustomClearText show={props.show} />,
      getStyles,
      innerProps: { ref, ...restInnerProps },
    } = props;
    return (
      <div {...restInnerProps} ref={ref} style={getStyles("clearIndicator", props) as CSSProperties}>
        {children}
      </div>
    );
  };

  return (
    <StyledAutoComplete>
      <ErrorWrapper
        active={active}
        title={title}
        showLock={showLock}
        error={error}
        caption={caption}
        captionColor={captionColor}
        captionAlign={captionAlign}
        name={name}
        onCaptionClick={onCaptionClick}
        required={required}
        className={className}
        type="select"
        helperBody={helperBody}
        helperTitle={helperTitle}
        titleColor={titleColor}
      >
        {creatable ? (
          <AsyncCreatableSelect
            data-name={name}
            {...selectProps}
            components={{
              ClearIndicator: (props) => <ClearIndicator show={value?.toString().length > 0 || false} {...props} />,
            }}
          />
        ) : (
          <AsyncSelect data-name={name} {...selectProps} />
        )}
      </ErrorWrapper>
    </StyledAutoComplete>
  );
};

export default Autocomplete;
